import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk/';
import { DebugService } from '@services/debug.service';
import { ViewModeService } from '@services/view-mode.service';
import {
  PortfolioBaseType,
  PortfolioComponentId,
  PortfolioDataResponse,
  PortfolioDisplay,
} from '@types';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BaseEntryProductComponent } from '../../base-entry-product-component';
import { PortfolioComponentConfig } from '../portfolio-component.config';
import { PortfolioService } from '../services/portfolio.service';

const logger = Logger.getLogger('PortfolioEntryComponent');

@Component({
  selector: 'ft-portfolio',
  templateUrl: './portfolio-entry.component.html',
  styleUrls: ['./portfolio-entry.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioEntryComponent
  extends BaseEntryProductComponent
  implements OnInit, OnDestroy {
  @Input() component!: BrComponent;
  @Input() page!: Page;
  @ViewChild('dynamicCmp', { read: ViewContainerRef, static: false }) container;
  debugMode$: Observable<boolean>;
  componentRef: ComponentRef<PortfolioDisplay>;
  componentType: PortfolioComponentId;
  componentConfig: PortfolioComponentConfig;
  isLoading = false;
  errorMsg: string;
  private unsubscribe$: Subject<void> = new Subject<void>();
  isEditMode = false;
  public containerWideClass: string;

  constructor(
    private viewModeService: ViewModeService,
    private portfolioService: PortfolioService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private changeDetector: ChangeDetectorRef,
    private debugService: DebugService
  ) {
    super();
    this.isEditMode = this.viewModeService.isEditMode();
  }

  ngOnInit() {
    super.ngOnInit();
    // this is only really necessary if component appears on a different page
    // this will usually appear on Product Details which already sets component IN_PROGRESS

    this.debugMode$ = this.debugService.isPortfolioDebug$();
    this.componentType = this.brConfig?.componentType as PortfolioComponentId;
    this.containerWideClass = this.brConfig?.hasContainerWideClass;
    this.componentConfig = PortfolioComponentConfig.getConfig(
      this.componentType
    );
    logger.debug('componentConfig', this.componentType, this.componentConfig);
    this.changeDetector.detectChanges(); // at this point show component if componentType exists

    if (this.componentConfig) {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
        this.componentConfig.component
      );
      this.container.clear();
      this.componentRef = this.container.createComponent(componentFactory);
      this.componentRef.instance.initialize(
        this.brConfig,
        this.component,
        this.page
      );
      this.isLoading = true;
      this.errorMsg = null;

      this.portfolioService
        .getPortfolioData$(this.componentConfig, this.brConfig.fundId)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(this.processData, this.handleError);

      if (this.componentConfig.baseType === PortfolioBaseType.PORTFOLIO_CHART) {
        this.portfolioService.portfolioSelection$
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(this.resizeChart);
      }

      this.portfolioService.setShowHistAvgeLifeMaturity(
        this.brConfig?.showHistAvgeLifeMaturity,
        this.componentType
      );

      this.changeDetector.detectChanges();
    }
  }

  processData = (response: PortfolioDataResponse): void => {
    logger.debug('processData()', response);
    if (response.success) {
      this.componentRef.instance.data = response.payload;
      if (this.componentRef.instance.processData) {
        this.componentRef.instance.processData();
      }
    }
    // NB: can still get a debug message if success
    this.errorMsg = response.message;
    this.isLoading = false;
    this.changeDetector.detectChanges();
  };

  handleError = (error: any): void => {
    logger.error('handleError()', error);
    this.isLoading = false;
    this.errorMsg = error.message;
    this.changeDetector.detectChanges();
  };

  resizeChart = (): void => {
    if (this.componentRef.instance.resizeChart) {
      this.componentRef.instance.resizeChart();
    }
  };

  ngOnDestroy() {
    // clean up subscription and dynamic component to avoid memory leaks
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.componentRef?.destroy();
  }
}
